import { z } from 'zod';
/**
 * Состояние агента (ТЗ §7.3).
 *
 * `never_connected` отделено от `offline` намеренно: «ещё ни разу не
 * подключался» и «подключался, но сейчас недоступен» требуют разных действий —
 * в первом случае проверяют установку, во втором связь.
 */
export const agentStatusSchema = z.enum(['never_connected', 'online', 'offline']);
/**
 * Площадка в списке обслуживаемых — идентификатор и название.
 *
 * Название едет вместе с идентификатором, потому что агент нигде
 * не показывается без своих площадок (макет, раздел 08), и запрашивать
 * их отдельным запросом ради одной колонки списка незачем.
 */
export const agentSiteSchema = z.object({
    id: z.uuid(),
    name: z.string(),
});
export const agentSchema = z.object({
    id: z.uuid(),
    /**
     * Площадки, которые обслуживает агент, по названию (ADR 032).
     *
     * Список, а не одна площадка: сегмент сети бывает общим для нескольких
     * площадок, и держать в нём по агенту на каждую значило бы опрашивать
     * один и тот же парк по разу за площадку.
     *
     * Пустой список — законное состояние: агент установлен, но обслуживать
     * ему пока нечего. Это видно на экране агентов, и это лучше, чем запрет
     * снять последнюю площадку.
     */
    sites: z.array(agentSiteSchema),
    name: z.string(),
    status: agentStatusSchema,
    version: z.string().nullable(),
    platform: z.string().nullable(),
    lastSeenAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    /** Ожидает первого подключения: одноразовый токен ещё не использован. */
    awaitingEnrollment: z.boolean(),
    /**
     * Обновляться самому (§11, фаза 9, задача 4).
     *
     * Выключается по площадке: обновление в разгар смены — решение
     * администратора, а не платформы.
     */
    autoUpdate: z.boolean(),
    /** Версия, до которой пытались обновить последний раз. */
    updateVersion: z.string().nullable(),
    /** Почему не вышло. `null` — либо не пробовали, либо вышло. */
    updateError: z.string().nullable(),
    updateAt: z.iso.datetime().nullable(),
});
export const createAgentSchema = z.object({
    name: z.string().trim().min(1).max(200),
});
/**
 * Создание агента сразу с его площадками (ADR 033).
 *
 * Отдельная схема, а не поле в `createAgentSchema`: у маршрута
 * `POST /sites/:siteId/agents` площадка стоит в пути, и второй список
 * площадок в теле означал бы там два источника одной и той же связи.
 *
 * Пустой список разрешён — тот же законный случай, что и в правке: агента
 * заводят под замену оборудования, площадки ему назначат отдельно.
 */
export const createAgentWithSitesSchema = createAgentSchema.extend({
    siteIds: z.array(z.uuid()).max(1000).default([]),
});
/**
 * Правка агента: название и список площадок.
 *
 * Смена площадок — не переименование: агент раздаёт мониторы своих площадок
 * и служит маршрутом к их узлам. Поэтому обязанности переезжают вместе
 * со списком, а конфигурация мониторов пересобирается сразу (ADR 010: она
 * рассылается полным списком, и новый список вытесняет прежний целиком).
 *
 * Список задаётся целиком, а не добавлением и снятием по одной: «снять
 * площадку» и «добавить площадку» пришлось бы применять в правильном
 * порядке, иначе площадка успела бы побывать без агента или с двумя.
 *
 * Пустой список разрешён — так агент снимается со всех площадок, не будучи
 * удалённым: при замене оборудования это ровно то, что нужно.
 */
export const updateAgentSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    siteIds: z.array(z.uuid()).max(1000),
    autoUpdate: z.boolean(),
})
    .partial();
/**
 * Ответ на создание агента. Одноразовый токен показывается **один раз**
 * (§8.2) и на сервере хранится только в виде хеша.
 */
export const createAgentResponseSchema = z.object({
    agent: agentSchema,
    enrollmentToken: z.string(),
});
// — Регистрация агента (§9.1) ————————————————————————————————
/** Сетевой интерфейс площадки, о котором агент сообщает при подключении. */
export const agentInterfaceSchema = z.object({
    name: z.string().max(64),
    ip: z.string().max(64),
});
export const enrollRequestSchema = z.object({
    enrollmentToken: z.string().min(16).max(256),
    version: z.string().max(32),
    /** Вида `linux/amd64`. */
    platform: z.string().max(32),
    hostname: z.string().max(255).optional(),
});
export const enrollResponseSchema = z.object({
    agentId: z.uuid(),
    /** Постоянный токен. Выдаётся один раз, хранится агентом в файле с правами 0600. */
    agentToken: z.string(),
});
// — Диагностика (§11, фаза 2, задача 10) ——————————————————————
export const testTcpRequestSchema = z.object({
    host: z.string().trim().min(1).max(255),
    port: z.number().int().min(1).max(65535),
    /** Предел ожидания соединения, мс. */
    timeoutMs: z.number().int().min(100).max(30_000).default(5000),
});
export const testTcpResponseSchema = z.object({
    ok: z.boolean(),
    /** Время до установления соединения, мс. */
    elapsedMs: z.number().nonnegative(),
    /** Причина отказа простым языком (§12): что произошло и что делать. */
    error: z.string().nullable(),
});
//# sourceMappingURL=agents.js.map